import { LitElement, html, css, nothing, type PropertyValues, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "../components/index.js";
import "./log-view.js";

type Json = null | boolean | number | string | Json[] | { [key: string]: Json };

// Joins the keys of a node's path; a key cannot hold it in practice.
const SEP = "\u0001";

function entries(v: Json[] | { [key: string]: Json }): [string, Json][] {
  return Array.isArray(v) ? v.map((item, i) => [String(i), item]) : Object.entries(v);
}

/** hasKids reports whether v is an object or array with at least one entry. */
function hasKids(v: Json): v is Json[] | { [key: string]: Json } {
  return typeof v === "object" && v !== null && entries(v).length > 0;
}

function collectPaths(v: Json, path: string, out: Set<string>): void {
  if (!hasKids(v)) return;
  out.add(path);
  for (const [k, child] of entries(v)) collectPaths(child, path + SEP + k, out);
}

/**
 * json-tree shows JSON text as a tree of collapsible objects and arrays. Only
 * open nodes are rendered, so a big file stays light until it is expanded.
 * "Raw text" shows the text as it is, which is also what invalid JSON gets.
 *
 *   <json-tree .text=${text}></json-tree>
 */
@customElement("json-tree")
export class JsonTree extends LitElement {
  @property() text = "";

  @state() private raw = false;
  /** Paths of the open nodes; "" is the root. */
  @state() private open = new Set<string>();

  private value: Json = null;
  private invalid = false;

  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      min-height: 0;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .bar {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      padding-bottom: var(--bino-space-sm);
      flex-shrink: 0;
    }
    .spacer {
      flex: 1;
    }
    .hint {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    log-view {
      flex: 1;
    }
    .tree {
      flex: 1;
      min-height: 0;
      overflow: auto;
      padding: var(--bino-space-md);
      background: var(--bino-surface-inset);
      border-radius: var(--bino-radius);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      line-height: 1.6;
    }
    .kids {
      margin-left: 1.25rem;
    }
    .leaf {
      display: flex;
      gap: 1ch;
      padding-left: 1rem;
    }
    summary {
      cursor: pointer;
    }
    summary:hover {
      background: var(--bino-surface-hover);
    }
    .meta {
      color: var(--bino-text-secondary);
    }
    .str {
      color: var(--bino-green-800);
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }
    .num {
      color: var(--bino-blue-800);
    }
    .lit {
      color: var(--bino-purple-800);
    }
  `;

  override willUpdate(changed: PropertyValues) {
    if (!changed.has("text")) return;
    try {
      this.value = JSON.parse(this.text) as Json;
      this.invalid = false;
    } catch {
      this.value = null;
      this.invalid = true;
    }
    this.open = new Set([""]);
  }

  private onToggle(path: string, e: Event) {
    const isOpen = (e.target as HTMLDetailsElement).open;
    if (isOpen === this.open.has(path)) return;
    const next = new Set(this.open);
    if (isOpen) next.add(path);
    else next.delete(path);
    this.open = next;
  }

  private expandAll() {
    const all = new Set<string>();
    collectPaths(this.value, "", all);
    this.open = all;
  }

  private renderValue(v: Json) {
    if (typeof v === "string") return html`<span class="str">"${v}"</span>`;
    if (typeof v === "number") return html`<span class="num">${v}</span>`;
    if (v === null || typeof v === "boolean") return html`<span class="lit">${String(v)}</span>`;
    return html`<span class="meta">${Array.isArray(v) ? "[]" : "{}"}</span>`;
  }

  private renderKids(v: Json[] | { [key: string]: Json }, path: string): TemplateResult[] {
    return entries(v).map(([key, child]) => this.renderEntry(key, child, path + SEP + key));
  }

  private renderEntry(key: string, v: Json, path: string): TemplateResult {
    if (!hasKids(v)) {
      return html`<div class="leaf"><span>${key}:</span>${this.renderValue(v)}</div>`;
    }
    const open = this.open.has(path);
    const size = entries(v).length;
    return html`<details ?open=${open} @toggle=${(e: Event) => this.onToggle(path, e)}>
      <summary>
        ${key}: <span class="meta">${Array.isArray(v) ? `[${size}]` : `{${size}}`}</span>
      </summary>
      ${open ? html`<div class="kids">${this.renderKids(v, path)}</div>` : nothing}
    </details>`;
  }

  override render() {
    if (this.invalid) {
      return html`<p class="hint">This is not valid JSON. Showing it as text.</p>
        <log-view .text=${this.text}></log-view>`;
    }
    return html`
      <div class="bar">
        ${this.raw
          ? nothing
          : html`<bn-button variant="ghost" size="sm" @click=${() => this.expandAll()}
                >Expand all</bn-button
              >
              <bn-button variant="ghost" size="sm" @click=${() => (this.open = new Set([""]))}
                >Collapse all</bn-button
              >`}
        <span class="spacer"></span>
        <bn-checkbox
          label="Raw text"
          .checked=${this.raw}
          @change=${(e: CustomEvent) => (this.raw = e.detail.checked)}
        ></bn-checkbox>
      </div>
      ${this.raw
        ? html`<log-view .text=${this.text}></log-view>`
        : html`<div class="tree" tabindex="0">
            ${hasKids(this.value) ? this.renderKids(this.value, "") : this.renderValue(this.value)}
          </div>`}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "json-tree": JsonTree;
  }
}
